import { useState } from "react";
import { Badge, Box, Card, Flex, Tabs, Text } from "@radix-ui/themes";
import { Link } from "react-router-dom";
import { Coins } from "lucide-react";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import Select from "@/components/ui/Select";
import { PaginatedResult } from "@/types/breeze/Any";
import {
  TenantBalanceHolder,
  UserBalanceLedgerEntry,
} from "@/types/breeze/AnythinkPay";
import {
  useTenantBalanceHolders,
  useTenantBalanceActivity,
} from "@/hooks/useAnythinkPayOffers";
import { useAuth } from "@/providers/AuthProvider";
import { formatDate } from "@/utils/date";
import { EmptyStateCard } from "./_shared";

type UnitFilter = "all" | "points" | "credit";

const formatTotal = (h: { unit_type: string; currency?: string | null; total: number }) =>
  h.unit_type === "credit"
    ? `${(h.total / 100).toFixed(2)} ${h.currency?.toUpperCase() ?? ""}`.trim()
    : `${h.total.toLocaleString()} pts`;

export default function Balances() {
  const [unit, setUnit] = useState<UnitFilter>("all");

  return (
    <Flex direction="column" gap="4">
      <Tabs.Root defaultValue="holders">
        <Tabs.List>
          <Tabs.Trigger value="holders">Holders</Tabs.Trigger>
          <Tabs.Trigger value="activity">Activity</Tabs.Trigger>
        </Tabs.List>

        <Box mt="3">
          <Tabs.Content value="holders">
            <Flex direction="column" gap="3">
              <Flex justify="end" align="center" gap="2">
                <Text size="1" color="gray">Unit</Text>
                <Box style={{ minWidth: 160 }}>
                  <Select
                    value={unit}
                    onChange={(v) => setUnit(v as UnitFilter)}
                    options={[
                      { value: "all", label: "All" },
                      { value: "points", label: "Points" },
                      { value: "credit", label: "Account credit" },
                    ]}
                  />
                </Box>
              </Flex>
              <HoldersTable unitType={unit} />
            </Flex>
          </Tabs.Content>

          <Tabs.Content value="activity">
            <ActivityTable />
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Flex>
  );
}

function HoldersTable({ unitType }: { unitType: UnitFilter }) {
  const { currentTenant } = useAuth();
  const { data, isLoading } = useTenantBalanceHolders(
    unitType === "all" ? undefined : unitType,
  );

  if (isLoading) return <LoadingScreen fullPage={false} />;

  const items = data ?? [];
  if (items.length === 0) {
    return (
      <EmptyStateCard
        icon={<Coins size={36} />}
        title="No balance holders yet"
        description="Once users earn points or credit through offer rewards or admin adjustments, they'll appear here ranked by total."
      />
    );
  }

  const columns: DataGridColumn<TenantBalanceHolder>[] = [
    {
      field: "user",
      header: "User",
      flex: 1.5,
      minWidth: 220,
      cellRenderer: (h) => (
        <Flex align="center" gap="2" style={{ minWidth: 0 }}>
          <Link
            to={`/org/${currentTenant?.id}/settings/users/${h.user_id}`}
            style={{ color: "var(--accent-11)", textDecoration: "none", fontWeight: 500 }}
          >
            {h.user
              ? `${h.user.first_name} ${h.user.last_name}`.trim()
              : `User #${h.user_id}`}
          </Link>
          {h.user?.email && (
            <Text
              size="1"
              color="gray"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {h.user.email}
            </Text>
          )}
        </Flex>
      ),
      valueGetter: (h) =>
        h.user ? `${h.user.first_name} ${h.user.last_name}`.trim() : `#${h.user_id}`,
    },
    {
      field: "unit_type",
      header: "Unit",
      flex: 0.7,
      minWidth: 120,
      cellRenderer: (h) => (
        <Badge size="1" variant="soft" color={h.unit_type === "points" ? "blue" : "green"}>
          {h.unit_type === "points" ? "Points" : `Credit ${h.currency?.toUpperCase() ?? ""}`.trim()}
        </Badge>
      ),
      valueGetter: (h) => `${h.unit_type} ${h.currency ?? ""}`.trim(),
    },
    {
      field: "total",
      header: "Total",
      flex: 0.8,
      minWidth: 140,
      cellRenderer: (h) => (
        <Text size="2" weight="bold">{formatTotal(h)}</Text>
      ),
      valueGetter: (h) => String(h.total),
    },
  ];

  const paginated: PaginatedResult<TenantBalanceHolder> = {
    items,
    page: 1,
    page_size: items.length,
    total_items: items.length,
    total_pages: 1,
    has_next_page: false,
    has_previous_page: false,
  };

  return (
    <Box style={{ height: 480 }}>
      <DataGrid<TenantBalanceHolder & { id: string }>
        data={{
          ...paginated,
          // DataGrid requires { id }; synthesise one from the natural key.
          items: items.map((h) => ({
            ...h,
            id: `${h.user_id}:${h.unit_type}:${h.currency ?? ""}`,
          })),
        }}
        columns={columns as DataGridColumn<TenantBalanceHolder & { id: string }>[]}
        storageKey="tenant-balance-holders"
        currentPage={1}
        currentPageSize={items.length}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        rowSelection="none"
        showPagination={false}
      />
    </Box>
  );
}

function ActivityTable() {
  const { currentTenant } = useAuth();
  const { data, isLoading } = useTenantBalanceActivity();

  if (isLoading) return <LoadingScreen fullPage={false} />;

  const items = data ?? [];
  if (items.length === 0) {
    return (
      <Card>
        <Flex direction="column" align="center" justify="center" gap="3" p="6">
          <Box style={{ color: "var(--gray-9)" }}>
            <Coins size={36} />
          </Box>
          <Text size="2" color="gray">
            No balance activity yet. Offer redemptions and admin adjustments
            will appear here as they happen.
          </Text>
        </Flex>
      </Card>
    );
  }

  const columns: DataGridColumn<UserBalanceLedgerEntry>[] = [
    {
      field: "created_at",
      header: "When",
      flex: 0.9,
      minWidth: 140,
      cellRenderer: (e) => (
        <Text size="2" color="gray">{formatDate(e.created_at)}</Text>
      ),
      valueGetter: (e) => e.created_at,
    },
    {
      field: "holder",
      header: "Holder",
      flex: 1.3,
      minWidth: 200,
      cellRenderer: (e) => (
        <Link
          to={`/org/${currentTenant?.id}/settings/users/${e.user_id}`}
          style={{ color: "var(--accent-11)", textDecoration: "none" }}
        >
          {e.holder
            ? `${e.holder.first_name} ${e.holder.last_name}`.trim()
            : `User #${e.user_id}`}
        </Link>
      ),
      valueGetter: (e) =>
        e.holder ? `${e.holder.first_name} ${e.holder.last_name}`.trim() : `#${e.user_id}`,
    },
    {
      field: "delta",
      header: "Change",
      flex: 0.8,
      minWidth: 130,
      cellRenderer: (e) => {
        const display = e.unit_type === "credit"
          ? `${e.delta >= 0 ? "+" : ""}${(e.delta / 100).toFixed(2)} ${e.currency?.toUpperCase() ?? ""}`.trim()
          : `${e.delta >= 0 ? "+" : ""}${e.delta.toLocaleString()} pts`;
        return (
          <Badge size="1" variant="soft" color={e.delta >= 0 ? "green" : "red"}>
            {display}
          </Badge>
        );
      },
      valueGetter: (e) => String(e.delta),
    },
    {
      field: "source",
      header: "Source",
      flex: 0.9,
      minWidth: 140,
      cellRenderer: (e) => (
        <Text size="2">{e.source.replace(/_/g, " ")}</Text>
      ),
    },
    {
      field: "reason",
      header: "Reason",
      flex: 1.4,
      minWidth: 200,
      cellRenderer: (e) => (
        <Text size="2">{e.reason ?? "—"}</Text>
      ),
    },
    {
      field: "by",
      header: "By",
      flex: 1,
      minWidth: 160,
      cellRenderer: (e) => (
        <Text size="2">
          {e.created_by
            ? `${e.created_by.first_name} ${e.created_by.last_name}`.trim()
            : e.created_by_user_id
            ? `#${e.created_by_user_id}`
            : "System"}
        </Text>
      ),
    },
  ];

  const paginated: PaginatedResult<UserBalanceLedgerEntry> = {
    items,
    page: 1,
    page_size: items.length,
    total_items: items.length,
    total_pages: 1,
    has_next_page: false,
    has_previous_page: false,
  };

  return (
    <Box style={{ height: 480 }}>
      <DataGrid<UserBalanceLedgerEntry>
        data={paginated}
        columns={columns}
        storageKey="tenant-balance-activity"
        currentPage={1}
        currentPageSize={items.length}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        rowSelection="none"
        showPagination={false}
      />
    </Box>
  );
}
